import type { ApplicantSourceType, ParentRelationshipType } from '@shared/types';

import { ApiError, apiFetch, createRequestId } from '../../lib/api';
import {
  DEFAULT_COUNTRY_CODE,
  LAUNCH_ACADEMIC_YEAR_ID,
  LAUNCH_CURRICULUM_ID,
} from '../../lib/constants';

export const SCHOOL_HOME = '__HOME_SCHOOL__';
export const SCHOOL_OTHER = '__OTHER__';

export interface RegistrationDraftResponse {
  payload: Record<string, unknown>;
  completedSections: string[];
}

export interface PartnerSchool {
  id: string;
  name: string;
  city: string;
}

export interface AcademicLevelOption {
  id: string;
  code: string;
  name: string;
  conventionalGradeLabel: string | null;
}

export interface DeclarationTemplate {
  id: string;
  code: string;
  version: number;
  bodyText: string;
  termsVersion: string;
  feePolicyVersion: string;
  examFeeNoteText: string | null;
  examFeeNoteVersion: number | null;
}

export interface RegistrationQuotationResult {
  quotationId: string | null;
  quotationNumber: string | null;
  quotationType: 'REGISTRATION';
  payableTotalMinor: string;
  currency: 'INR';
  shortCircuited: boolean;
  applicationStatus: string;
}

export interface LearnerSummary {
  learnerId: string;
  enrolmentNumber: string;
  firstName: string;
  middleName: string | null;
  lastName: string;
  gender: string | null;
  applicationId: string;
  applicationStatus: string;
  offeringId: string;
  academicBagId: string | null;
  bagStatus: string | null;
  admissionId: string | null;
  academicLevel: { id: string; code: string; name: string } | null;
}

export interface RegistrationFormState {
  learnerFirstName: string;
  learnerMiddleName: string;
  learnerLastName: string;
  dateOfBirth: string;
  gender: string;
  academicLevelId: string;
  currentSchool: string;
  otherSchoolName: string;
  schoolAdmissionNumber: string;
  city: string;
  state: string;
  country: string;
  pincode: string;
  parentFirstName: string;
  parentMiddleName: string;
  parentLastName: string;
  relationshipToLearner: ParentRelationshipType | '';
  parentMobileCountryCode: string;
  parentMobile: string;
  mobileIsWhatsapp: boolean;
  whatsappMobile: string;
}

export const EMPTY_FORM: RegistrationFormState = {
  learnerFirstName: '',
  learnerMiddleName: '',
  learnerLastName: '',
  dateOfBirth: '2011-06-01',
  gender: '',
  academicLevelId: '',
  currentSchool: '',
  otherSchoolName: '',
  schoolAdmissionNumber: '',
  city: '',
  state: 'Karnataka',
  country: DEFAULT_COUNTRY_CODE,
  pincode: '',
  parentFirstName: '',
  parentMiddleName: '',
  parentLastName: '',
  relationshipToLearner: '',
  parentMobileCountryCode: '+91',
  parentMobile: '',
  mobileIsWhatsapp: true,
  whatsappMobile: '',
};

export function resolveSchoolSelection(
  currentSchool: string,
  otherSchoolName: string,
): {
  applicantSourceType: ApplicantSourceType;
  schoolId: string | null;
  currentSchoolName: string | null;
} {
  if (currentSchool === SCHOOL_HOME) {
    return { applicantSourceType: 'HOMESCHOOLED', schoolId: null, currentSchoolName: null };
  }
  if (currentSchool === SCHOOL_OTHER) {
    return {
      applicantSourceType: 'NON_PARTNER_PRIVATE_SCHOOL',
      schoolId: null,
      currentSchoolName: otherSchoolName.trim() || null,
    };
  }
  return {
    applicantSourceType: 'PARTNER_SCHOOL',
    schoolId: currentSchool,
    currentSchoolName: null,
  };
}

export function formStateFromDraft(payload: Record<string, unknown>): RegistrationFormState {
  return {
    ...EMPTY_FORM,
    learnerFirstName: String(payload.learnerFirstName ?? ''),
    learnerMiddleName: String(payload.learnerMiddleName ?? ''),
    learnerLastName: String(payload.learnerLastName ?? ''),
    dateOfBirth: String(payload.dateOfBirth ?? EMPTY_FORM.dateOfBirth),
    gender: String(payload.gender ?? ''),
    academicLevelId: String(payload.academicLevelId ?? ''),
    currentSchool: String(payload.currentSchool ?? ''),
    otherSchoolName: String(payload.otherSchoolName ?? ''),
    schoolAdmissionNumber: String(payload.schoolAdmissionNumber ?? ''),
    city: String(payload.city ?? ''),
    state: String(payload.state ?? EMPTY_FORM.state),
    country: String(payload.country ?? DEFAULT_COUNTRY_CODE),
    pincode: String(payload.pincode ?? ''),
    parentFirstName: String(payload.parentFirstName ?? ''),
    parentMiddleName: String(payload.parentMiddleName ?? ''),
    parentLastName: String(payload.parentLastName ?? ''),
    relationshipToLearner: (payload.relationshipToLearner as ParentRelationshipType | '') ?? '',
    parentMobileCountryCode: String(payload.parentMobileCountryCode ?? '+91'),
    parentMobile: String(payload.parentMobile ?? ''),
    mobileIsWhatsapp: payload.mobileIsWhatsapp !== false,
    whatsappMobile: String(payload.whatsappMobile ?? ''),
  };
}

export function draftPatchFromForm(form: RegistrationFormState): Record<string, unknown> {
  return { ...form };
}

/** Profile fields used to prefill the registration form for a returning parent. */
export interface ParentProfilePrefill {
  firstName: string | null;
  middleName: string | null;
  lastName: string | null;
  relationshipToLearner: string;
  countryCode: string;
  mobile: string | null;
  whatsappCountryCode: string | null;
  whatsappNumber: string | null;
}

function isBlank(value: string): boolean {
  return value.trim().length === 0;
}

/** Fill empty parent/guardian fields from the signed-in parent's saved profile. */
export function applyParentProfileDefaults(
  form: RegistrationFormState,
  profile: ParentProfilePrefill,
): RegistrationFormState {
  const profileMobile = profile.mobile?.trim() ?? '';
  const profileWhatsapp = profile.whatsappNumber?.trim() ?? '';
  const whatsappMatchesMobile = Boolean(
    profileMobile && profileWhatsapp && profileWhatsapp === profileMobile,
  );

  let mobileIsWhatsapp = form.mobileIsWhatsapp;
  let whatsappMobile = form.whatsappMobile;
  if (isBlank(form.parentMobile) && profileMobile) {
    if (whatsappMatchesMobile || !profileWhatsapp) {
      mobileIsWhatsapp = true;
      whatsappMobile = '';
    } else {
      mobileIsWhatsapp = false;
      whatsappMobile = profileWhatsapp;
    }
  }

  return {
    ...form,
    parentFirstName: isBlank(form.parentFirstName) ? (profile.firstName?.trim() ?? '') : form.parentFirstName,
    parentMiddleName: isBlank(form.parentMiddleName) ? (profile.middleName?.trim() ?? '') : form.parentMiddleName,
    parentLastName: isBlank(form.parentLastName) ? (profile.lastName?.trim() ?? '') : form.parentLastName,
    relationshipToLearner: form.relationshipToLearner
      || (profile.relationshipToLearner as RegistrationFormState['relationshipToLearner'])
      || '',
    parentMobileCountryCode: isBlank(form.parentMobileCountryCode)
      ? (profile.countryCode || '+91')
      : form.parentMobileCountryCode,
    parentMobile: isBlank(form.parentMobile) ? profileMobile : form.parentMobile,
    mobileIsWhatsapp,
    whatsappMobile,
  };
}

export function hasParentProfilePrefill(profile: ParentProfilePrefill): boolean {
  return Boolean(
    profile.firstName?.trim()
    || profile.lastName?.trim()
    || profile.mobile?.trim(),
  );
}

export async function fetchRegistrationDraft(): Promise<RegistrationDraftResponse> {
  return apiFetch<RegistrationDraftResponse>('/registration/draft');
}

export async function patchRegistrationDraft(
  patch: Record<string, unknown>,
): Promise<RegistrationDraftResponse> {
  return apiFetch<RegistrationDraftResponse>('/registration/draft', {
    method: 'PATCH',
    body: JSON.stringify(patch),
  });
}

export async function fetchPartnerSchools(academicLevelId: string): Promise<{
  items: PartnerSchool[];
  homeSchoolAvailable: boolean;
}> {
  return apiFetch<{ items: PartnerSchool[]; homeSchoolAvailable?: boolean }>(
    `/registration/schools?academicLevelId=${encodeURIComponent(academicLevelId)}`,
  ).then((result) => ({
    items: result.items,
    homeSchoolAvailable: result.homeSchoolAvailable !== false,
  }));
}

export async function fetchRegistrationLevels(): Promise<AcademicLevelOption[]> {
  const result = await apiFetch<{ items: AcademicLevelOption[] }>('/registration/levels');
  return result.items;
}

export async function fetchActiveDeclaration(kind = 'REGISTRATION_TERMS'): Promise<DeclarationTemplate> {
  const query = kind === 'REGISTRATION_TERMS' ? '' : `?kind=${encodeURIComponent(kind)}`;
  return apiFetch<DeclarationTemplate>(`/declarations/active${query}`);
}

export async function acceptDocumentSelfDeclaration(
  applicationId: string,
  input: {
    declarationTemplateId: string;
    declarationVersion: number;
    acceptedByName: string;
  },
): Promise<void> {
  await apiFetch(`/applications/${applicationId}/document-self-declaration`, {
    method: 'POST',
    body: JSON.stringify({
      ...input,
      acceptedAt: new Date().toISOString(),
    }),
  });
}

export interface RegistrationSubmitResult extends RegistrationQuotationResult {
  applicationId: string;
}

export class RegistrationStepError extends Error {
  constructor(
    readonly step: string,
    readonly source: unknown,
  ) {
    super(`Registration failed while saving ${step}`);
    this.name = 'RegistrationStepError';
  }
}

async function runRegistrationStep<T>(step: string, action: () => Promise<T>): Promise<T> {
  try {
    return await action();
  } catch (cause) {
    throw new RegistrationStepError(step, cause);
  }
}

export async function submitRegistration(input: {
  form: RegistrationFormState;
  parentGuardianId: string;
  acceptedByName: string;
  declaration: DeclarationTemplate;
}): Promise<RegistrationSubmitResult> {
  const { form, parentGuardianId, acceptedByName, declaration } = input;
  if (!form.currentSchool) throw new Error('Select the learner’s current school');
  if (!form.academicLevelId) throw new Error('Select the learner’s current grade');
  if (!form.relationshipToLearner) throw new Error('Select your relationship to the learner');

  const school = resolveSchoolSelection(form.currentSchool, form.otherSchoolName);
  if (school.applicantSourceType === 'NON_PARTNER_PRIVATE_SCHOOL' && !school.currentSchoolName) {
    throw new Error('Enter the current school name');
  }
  if (school.applicantSourceType !== 'HOMESCHOOLED' && !form.schoolAdmissionNumber.trim()) {
    throw new Error('Enter the school admission number');
  }

  const whatsappCountryCode = form.mobileIsWhatsapp
    ? DEFAULT_COUNTRY_CODE
    : (form.whatsappMobile ? DEFAULT_COUNTRY_CODE : null);
  const whatsappNumber = form.mobileIsWhatsapp
    ? form.parentMobile
    : (form.whatsappMobile || null);

  await runRegistrationStep('your contact details', () =>
    apiFetch('/registration/parent-guardian', {
      method: 'PATCH',
      body: JSON.stringify({
        firstName: form.parentFirstName,
        middleName: form.parentMiddleName || null,
        lastName: form.parentLastName,
        relationshipToLearner: form.relationshipToLearner,
        countryCode: DEFAULT_COUNTRY_CODE,
        mobile: form.parentMobile,
        whatsappCountryCode,
        whatsappNumber,
      }),
    }),
  );

  const learner = await runRegistrationStep('learner details', () =>
    apiFetch<{ id: string }>('/learners', {
      method: 'POST',
      body: JSON.stringify({
        firstName: form.learnerFirstName,
        middleName: form.learnerMiddleName || null,
        lastName: form.learnerLastName,
        gender: form.gender || null,
        dateOfBirth: form.dateOfBirth,
        applicantSourceType: school.applicantSourceType,
        currentSchoolName: school.currentSchoolName,
        schoolAdmissionNumber: school.applicantSourceType === 'HOMESCHOOLED'
          ? null
          : form.schoolAdmissionNumber.trim(),
        city: form.city,
        state: form.state,
        country: form.country,
        pincode: form.pincode,
        academicYearId: LAUNCH_ACADEMIC_YEAR_ID,
        schoolId: school.schoolId,
      }),
    }),
  );

  const application = await runRegistrationStep('application', () =>
    apiFetch<{ id: string }>('/applications', {
      method: 'POST',
      body: JSON.stringify({
        learnerId: learner.id,
        primaryParentId: parentGuardianId,
        academicYearId: LAUNCH_ACADEMIC_YEAR_ID,
        curriculumId: LAUNCH_CURRICULUM_ID,
        applicantSourceType: school.applicantSourceType,
        schoolId: school.schoolId,
        academicLevelId: form.academicLevelId,
      }),
    }),
  );

  await runRegistrationStep('declaration acceptance', () =>
    apiFetch(`/applications/${application.id}/declaration-acceptance`, {
      method: 'POST',
      body: JSON.stringify({
        declarationTemplateId: declaration.id,
        declarationVersion: declaration.version,
        acceptedByName,
        acceptedAt: new Date().toISOString(),
      }),
    }),
  );

  const quotation = await runRegistrationStep('registration fee quotation', () =>
    apiFetch<RegistrationQuotationResult>(
      `/applications/${application.id}/registration-quotation`,
      { method: 'POST' },
    ),
  );

  return { ...quotation, applicationId: application.id };
}

export async function fetchMyLearners(): Promise<LearnerSummary[]> {
  const result = await apiFetch<{ items: LearnerSummary[] }>('/parents/me/learners');
  return result.items;
}

export function findRegistrationPendingPayment(
  learners: LearnerSummary[],
): LearnerSummary | undefined {
  return learners.find((learner) => learner.applicationStatus === 'REGISTRATION_PENDING_PAYMENT');
}

export function registrationPaymentPath(applicationId: string): string {
  return `/register/payment?applicationId=${encodeURIComponent(applicationId)}`;
}

/** Where to send a parent immediately after sign-in. */
export async function resolvePostLoginPath(): Promise<string> {
  const learners = await fetchMyLearners();
  const pending = findRegistrationPendingPayment(learners);
  if (pending) return registrationPaymentPath(pending.applicationId);
  return '/dashboard';
}

export interface QuotationView {
  id: string;
  quotationNumber?: string;
  quotationType?: string;
  payableTotalMinor: string;
  validUntil?: string | null;
  subtotalMinor?: string;
  adjustmentTotalMinor?: string;
  snapshot?: {
    content?: { cambridgeExamNoteVersion?: number | null };
    notices?: Array<{ code: string; text: string; version?: number }>;
    totals?: { payableTotalMinor?: string };
    pricing?: Array<{
      productCode: string;
      grossAmountMinor: string;
      quantity: number;
    }>;
  };
}

export async function fetchApplicationQuotation(
  applicationId: string,
  quotationType?: 'REGISTRATION' | 'SUBJECT_FEES',
): Promise<QuotationView> {
  const query = quotationType ? `?type=${quotationType}` : '';
  return apiFetch<QuotationView>(`/applications/${applicationId}/quotation${query}`);
}

function isQuotationExpired(validUntil?: string | null): boolean {
  return Boolean(validUntil && new Date(validUntil) < new Date());
}

/** Issue or refresh the registration quotation, then load it for display. */
export async function ensureRegistrationQuotation(applicationId: string): Promise<QuotationView> {
  await apiFetch<RegistrationQuotationResult>(
    `/applications/${applicationId}/registration-quotation`,
    { method: 'POST' },
  );
  return fetchApplicationQuotation(applicationId, 'REGISTRATION');
}

/** Load a payable subject-fee quotation, regenerating when missing or expired. */
export async function ensureSubjectFeeQuotation(applicationId: string): Promise<QuotationView> {
  try {
    const quotation = await fetchApplicationQuotation(applicationId, 'SUBJECT_FEES');
    if (!isQuotationExpired(quotation.validUntil)) return quotation;
  } catch (err) {
    if (!(err instanceof ApiError && err.problem.status === 404)) throw err;
  }
  await generateSubjectFeeQuotation(applicationId);
  return fetchApplicationQuotation(applicationId, 'SUBJECT_FEES');
}

export async function generateSubjectFeeQuotation(
  applicationId: string,
): Promise<{ quotationId: string; payableTotalMinor: string }> {
  return apiFetch(`/applications/${applicationId}/subject-fee-quotation`, { method: 'POST' });
}

export async function acceptPartialApproval(applicationId: string): Promise<void> {
  await apiFetch(`/applications/${applicationId}/partial-approval/accept`, { method: 'POST' });
}

export interface AdmissionView {
  id: string;
  applicationId: string;
  status: string;
  provisionalAt: string | null;
  confirmedAt: string | null;
  enrolmentNumber: string;
}

export async function fetchApplicationAdmission(applicationId: string): Promise<AdmissionView> {
  return apiFetch<AdmissionView>(`/applications/${applicationId}/admission`);
}

export interface AdmissionAcknowledgement {
  enrolmentNumber: string;
  status: string;
  provisional: boolean;
  provisionalAt: string | null;
  confirmedAt: string | null;
  summary: Record<string, unknown> | null;
  disclaimer: string;
}

export async function fetchAdmissionAcknowledgement(
  admissionId: string,
): Promise<AdmissionAcknowledgement> {
  return apiFetch<AdmissionAcknowledgement>(`/admissions/${admissionId}/acknowledgement`);
}

export interface PaymentOrderResult {
  paymentOrderId: string;
  orderNumber: string;
  gateway: string;
  gatewayOrderId: string;
  amountMinor: string;
  currency: string;
  amountDisplay: string;
  checkout: Record<string, string>;
  prefill: {
    name: string;
    email: string;
    contact: string;
  } | null;
  expiresAt: string;
  status: string;
  attemptNo: number;
}

export async function createPaymentOrder(quotationId: string): Promise<PaymentOrderResult> {
  return apiFetch<PaymentOrderResult>(
    '/payment-orders',
    {
      method: 'POST',
      body: JSON.stringify({ quotationId }),
    },
    { idempotencyKey: createRequestId() },
  );
}

export async function pollPaymentOrderStatus(orderId: string): Promise<{ status: string }> {
  return apiFetch<{ status: string }>(`/payment-orders/${orderId}/status?pollGateway=true`);
}

export function formatLevelLabel(level: AcademicLevelOption): string {
  return level.conventionalGradeLabel
    ? `${level.name} (${level.conventionalGradeLabel})`
    : level.name;
}

export const RELATIONSHIP_OPTIONS: Array<{ value: ParentRelationshipType; label: string }> = [
  { value: 'FATHER', label: 'Father' },
  { value: 'MOTHER', label: 'Mother' },
  { value: 'LEGAL_GUARDIAN', label: 'Legal guardian' },
];
